/**
 * Ombor API — ma'lumotlar faqat backenddan.
 * 1) GET /api/warehouse/overview?role=... — asosiy manba (backend barcha yozuvlarni qaytaradi).
 * 2) Bo'sh qaytsa: GET /api/tenants + GET /api/tenants/:id (har bir tenant_owner products) orqali yig'iladi (ham backend).
 * Qanday format: { data: [ { tenant_id, tenant_name, region_id, region, district_id, district, product_id, product_name, stock, sold_count } ] }
 */
import api from './api';
import { endpoints } from './endpoints';
import { getTenants } from './tenantsApi';
import { getTenantDetail } from './tenantsApi';
import { normalizeSaleUnit } from '../utils/productSaleUnit';
import { resolveMediaUrl } from '../utils/resolveMediaUrl';
import {
  getFifoBatch,
  resolveBatchStockValueTotal,
  resolveFifoPurchasePrice,
} from '../utils/warehouseBatchMetrics';

function toCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function mapKeys(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(mapKeys);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [toCamel(k), mapKeys(v)])
  );
}

function normalizeSaleOptionsLike(input) {
  if (Array.isArray(input)) return input.map((row) => mapKeys(row));
  if (input && typeof input === 'object') {
    const vals = Object.values(input);
    if (vals.length > 0 && vals.every((v) => v && typeof v === 'object')) {
      return vals.map((row) => mapKeys(row));
    }
  }
  if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed.map((row) => mapKeys(row)) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function pickFirstSaleOptions(...sources) {
  for (const src of sources) {
    const opts = normalizeSaleOptionsLike(src);
    if (opts.length > 0) return opts;
  }
  return [];
}

/**
 * Backend /api/warehouse/overview dan ma'lumot olish (dinamik).
 * params: { role?: string, region_id?: string } — backend filtrlash uchun.
 */
export async function getWarehouseOverview(params = {}) {
  const normalizeRole = (v) => String(v ?? '').trim().toLowerCase();
  let role = normalizeRole(params?.role);
  if (!role && endpoints.user) {
    try {
      const meRes = await api.get(endpoints.user);
      const meRaw = meRes?.data ?? meRes;
      role = normalizeRole(meRaw?.role);
    } catch {
      role = '';
    }
  }

  // tenant_owner va cashier uchun ham /api/warehouse/overview dan foydalanamiz —
  // backend endi bu rollarni to'g'ri boshqaradi (tenantScopedOverview + batches).
  // Agar endpoint xato qaytarsa, getWarehouseOverviewFromTenants fallback ishga tushadi.

  const rawParams = { ...(params || {}) };
  const wantBatches =
    rawParams.include_batches === 1 ||
    rawParams.include_batches === true ||
    rawParams.includeBatches === 1 ||
    rawParams.includeBatches === true;
  const skipBatches =
    rawParams.include_batches === 0 ||
    rawParams.include_batches === false ||
    rawParams.includeBatches === 0 ||
    rawParams.includeBatches === false;
  const apiParams = { ...rawParams };
  // lite=1: faqat sold aggregate’larni o‘tkazadi; partiyalar include_batches bilan boshqariladi (default: ko‘rsatiladi).
  if (!skipBatches && !wantBatches) {
    apiParams.include_batches = 1;
  }
  if (apiParams.lite !== 0 && apiParams.lite !== false) {
    apiParams.lite = 1;
  }

  try {
    const res = await api.get(endpoints.warehouseOverview, { params: apiParams });
    const raw = res?.data ?? res;
    const list = raw?.data ?? raw?.items ?? (Array.isArray(raw) ? raw : []);
    const rows = (Array.isArray(list) ? list : []).map((item) => {
      /** Backend `sale_price` (raqam) va `salePrice` (formatlangan qator) bir xil camelCase ga tushganda string raqamni bosib yuboradi. */
      const firstFinitePre = (...vals) => {
        for (const v of vals) {
          if (v == null || v === '') continue;
          const n = Number(v);
          if (Number.isFinite(n)) return n;
        }
        return null;
      };
      const overviewSaleNum = firstFinitePre(item.sale_price);
      const overviewPurchaseNum = firstFinitePre(item.purchase_price);

      const firstFinite = (...vals) => {
        for (const v of vals) {
          if (v == null || v === '') continue;
          const n = Number(v);
          if (Number.isFinite(n)) return n;
        }
        return null;
      };

      const m = mapKeys(item);
      const p = (m && typeof m === 'object' && m.product && typeof m.product === 'object') ? m.product : null;

      const baseUnitRaw =
        m.saleUnit ??
        m.sale_unit ??
        m.unit_type ??
        m.unitType ??
        m.measure_unit ??
        m.measureUnit ??
        p?.saleUnit ??
        p?.sale_unit ??
        p?.unit_type ??
        p?.unitType;
      const baseUnit = normalizeSaleUnit(baseUnitRaw);

      /** Raw `sale_options` — mapKeys dan oldin (backend overview endi yuboradi); aks holda camelCase. */
      const saleOptions = pickFirstSaleOptions(
        item.sale_options,
        m.saleOptions,
        m.sale_choices,
        m.saleChoices,
        m.variants,
        p?.saleOptions,
        p?.sale_choices,
        p?.saleChoices,
        p?.variants,
      );

      const saleOptionsStockBase = saleOptions.reduce((sum, opt) => {
        const stockRaw = firstFinite(opt?.stock, opt?.quantity, opt?.remaining);
        const stockVal = stockRaw != null ? Number(stockRaw) : 0;
        if (!Number.isFinite(stockVal) || stockVal <= 0) return sum;
        const unit = normalizeSaleUnit(
          opt?.saleUnit ?? opt?.sale_unit ?? opt?.unitType ?? opt?.unit_type ?? baseUnit
        );
        const ufRaw = firstFinite(opt?.unitFactor, opt?.unit_factor, 1);
        const uf = Math.max(1, Math.round(Number(ufRaw ?? 1) || 1));
        return sum + (unit === 'size' ? stockVal : stockVal * uf);
      }, 0);

      /** Overview qatorida `stock: 0` — haqiqiy nol; nested product fallback qo‘llanmaydi. */
      const pickWithProductFallback = (rowCandidates, productCandidates) => {
        const rowVal = firstFinite(...rowCandidates);
        if (rowVal != null) return rowVal;
        const prodVal = firstFinite(...productCandidates);
        return prodVal != null ? prodVal : 0;
      };

      const primaryChoice = saleOptions.find((x) => {
        const u = normalizeSaleUnit(
          x?.saleUnit ?? x?.sale_unit ?? x?.unitType ?? x?.unit_type ?? x?.measureUnit ?? x?.measure_unit
        );
        return u && u === baseUnit;
      }) || saleOptions[0] || null;

      const inv =
        m.inventory && typeof m.inventory === 'object'
          ? m.inventory
          : m.inventoryItem && typeof m.inventoryItem === 'object'
            ? m.inventoryItem
            : m.stockItem && typeof m.stockItem === 'object'
              ? m.stockItem
              : null;

      const rowStockCandidates = [
        m.stock,
        m.quantity,
        m.qty,
        m.stockQty,
        m.stockQuantity,
        m.currentStock,
        m.remaining,
        m.remainingQty,
        m.remainingQuantity,
        m.balance,
        m.left,
        m.inStock,
        m.available,
        m.availableQuantity,
        m.onHand,
        m.on_hand,
        m.qoldiq,
        inv?.stock,
        inv?.quantity,
        inv?.qty,
        inv?.onHand,
        inv?.on_hand,
        primaryChoice?.stock,
        primaryChoice?.quantity,
        primaryChoice?.remaining,
      ];

      const productStockCandidates = [p?.quantity, p?.stock, p?.remaining, p?.qty, p?.currentStock, p?.current_stock];

      const rowPurchaseCandidates = [
        overviewPurchaseNum,
        item.purchase_price,
        m.purchasePrice,
        m.purchase_price,
        m.costPrice,
        m.cost_price,
        m.priceBuy,
        m.price_buy,
        m.buyPrice,
        m.buy_price,
        m.pricePurchase,
        m.price_purchase,
        m.purchase,
        primaryChoice?.purchasePrice,
        primaryChoice?.purchase_price,
        primaryChoice?.costPrice,
        primaryChoice?.cost_price,
      ];

      const productPurchaseCandidates = [
        p?.purchasePrice,
        p?.purchase_price,
        p?.costPrice,
        p?.cost_price,
        p?.priceBuy,
        p?.price_buy,
      ];

      const rowSaleCandidates = [
        overviewSaleNum,
        item.sale_price,
        m.salePrice,
        m.sale_price,
        m.priceSell,
        m.price_sell,
        m.sellPrice,
        m.sell_price,
        m.price,
        m.sale,
        primaryChoice?.salePrice,
        primaryChoice?.sale_price,
        primaryChoice?.priceSell,
        primaryChoice?.price_sell,
        primaryChoice?.price,
      ];

      const productSaleCandidates = [p?.salePrice, p?.sale_price, p?.priceSell, p?.price_sell, p?.price];

      // Backendlar turlicha nomlar qaytarishi mumkin — jadvalda asosiy qoldiq/narxlar ko‘rinishi uchun.
      const authoritativeStock = firstFinite(
        m.stock,
        m.quantity,
        m.qty,
        inv?.stock,
        inv?.quantity,
        inv?.qty,
      );
      let stock =
        authoritativeStock != null
          ? authoritativeStock
          : pickWithProductFallback(rowStockCandidates, productStockCandidates);
      // 0 haqiqiy qoldiq bo‘lishi mumkin (sotuvdan keyin); variantlar JSONi eskirgan bo‘lsa,
      // saleOptionsStockBase ni qo‘yish «qayta paydo bo‘lgan qoldiq» xatosini beradi — faqat qiymat umuman yo‘q bo‘lsa.
      if ((stock == null || stock === '') && saleOptionsStockBase > 0) {
        stock = saleOptionsStockBase;
      }
      const stockNum = Number(stock);
      const normalizedStock = Number.isFinite(stockNum) ? Math.max(0, stockNum) : 0;
      stock = normalizedStock;
      let purchasePrice = pickWithProductFallback(rowPurchaseCandidates, productPurchaseCandidates);
      let salePrice = pickWithProductFallback(rowSaleCandidates, productSaleCandidates);
      const rawBatches = item.batches ?? m.batches;
      const batches =
        normalizedStock <= 0
          ? []
          : Array.isArray(rawBatches)
        ? rawBatches.map((b) => ({
            id: Number(b?.id ?? 0),
            purchasePrice: Number(b?.purchase_price ?? b?.purchasePrice ?? 0),
            salePrice: Number(b?.sale_price ?? b?.salePrice ?? 0),
            quantityRemaining: Number(b?.quantity_remaining ?? b?.quantityRemaining ?? 0),
            quantityInitial: Number(b?.quantity_initial ?? b?.quantityInitial ?? 0),
            createdAt: b?.created_at ?? b?.createdAt ?? null,
            supplierName: b?.supplier_name ?? b?.supplierName ?? null,
            supplierPhone: b?.supplier_phone ?? b?.supplierPhone ?? null,
            purchasePaid: b?.purchase_paid ?? b?.purchasePaid ?? true,
            expenseId: b?.expense_id ?? b?.expenseId ?? null,
            sourceType: b?.source_type ?? b?.sourceType ?? null,
          }))
            : [];
      if (batches.length > 0) {
        const batchSum = batches.reduce((s, b) => s + (Number(b.quantityRemaining) || 0), 0);
        if (batchSum > 0) {
          stock = batchSum;
        }
      }
      const stockAfterBatches = Number(stock);
      const normalizedStockFinal = Number.isFinite(stockAfterBatches) ? Math.max(0, stockAfterBatches) : 0;
      stock = normalizedStockFinal;
      const fifoPurchase =
        normalizedStockFinal <= 0
          ? null
          : resolveFifoPurchasePrice({
              stock: normalizedStockFinal,
              fifoPurchasePrice: item.fifo_purchase_price ?? m.fifoPurchasePrice,
              fifo_purchase_price: item.fifo_purchase_price ?? m.fifoPurchasePrice,
              batches,
              purchasePrice,
            });
      const batchStockValueTotal =
        normalizedStockFinal <= 0
          ? null
          : resolveBatchStockValueTotal({
              stock: normalizedStockFinal,
              batchStockValueTotal: item.batch_stock_value_total ?? m.batchStockValueTotal,
              batch_stock_value_total: item.batch_stock_value_total ?? m.batchStockValueTotal,
              batches,
            });
      if (fifoPurchase != null && fifoPurchase > 0) {
        purchasePrice = fifoPurchase;
      }
      const fifoFromBatch = getFifoBatch(batches);
      if (fifoFromBatch?.salePrice > 0) {
        salePrice = fifoFromBatch.salePrice;
      }
      const salePriceNum = Number(salePrice);
      if ((!Number.isFinite(salePriceNum) || salePriceNum === 0) && saleOptions.length > 0) {
        const primaryOpt = saleOptions.find((x) => {
          const u = normalizeSaleUnit(
            x?.saleUnit ?? x?.sale_unit ?? x?.unitType ?? x?.unit_type ?? x?.measureUnit ?? x?.measure_unit
          );
          return u && u === baseUnit;
        });
        const pr = primaryOpt
          ? Math.max(
              0,
              Math.round(Number(firstFinite(primaryOpt.price, primaryOpt.salePrice, primaryOpt.sale_price) || 0))
            )
          : 0;
        if (pr > 0) salePrice = pr;
      }

      return {
        tenantId: String(m.tenantId ?? m.tenant_id ?? ''),
        tenantName: m.tenantName ?? m.tenant_name ?? m.companyName ?? '—',
        regionId: String(m.regionId ?? m.region_id ?? ''),
        region: m.region ?? m.regionName ?? '—',
        districtId: String(m.districtId ?? m.district_id ?? ''),
        district: m.district ?? m.districtName ?? '—',
        branchId: String(m.branchId ?? m.branch_id ?? ''),
        branchName: m.branchName ?? m.branch_name ?? '—',
        productId: (() => {
          const raw =
            item.product_id ??
            m.product_id ??
            m.productId ??
            inv?.id ??
            m.id ??
            '';
          const n = Number(raw);
          if (Number.isFinite(n) && n > 0) return String(Math.round(n));
          const nested = Number(p?.product_id ?? p?.productId ?? '');
          if (Number.isFinite(nested) && nested > 0) return String(Math.round(nested));
          return '';
        })(),
        productName: m.productName ?? m.product_name ?? m.name ?? p?.name ?? '—',
        barcode: (m.barcode ?? p?.barcode) != null ? String(m.barcode ?? p?.barcode) : null,
        sku: (m.sku ?? p?.sku) != null ? String(m.sku ?? p?.sku) : null,
        markingCode: (m.markingCode ?? m.marking_code ?? p?.markingCode ?? p?.marking_code) != null
          ? String(m.markingCode ?? m.marking_code ?? p?.markingCode ?? p?.marking_code)
          : null,
        stock,
        stockHistory: Array.isArray(m.stockHistory ?? m.stock_history)
          ? (m.stockHistory ?? m.stock_history)
              .map((v) => Number(v))
              .filter((v) => Number.isFinite(v) && v >= 0)
          : [],
        soldCount: Number(
          m.soldCount ??
            m.sold_count ??
            m.sold ??
            m.totalSold ??
            m.total_sold ??
            p?.soldCount ??
            p?.sold_count ??
            p?.sold ??
            primaryChoice?.soldCount ??
            primaryChoice?.sold_count ??
            0
        ),
        soldValue: (() => {
          const v = firstFinite(m.soldValue, m.sold_value, item.sold_value, item.soldValue);
          return v != null && v >= 0 ? Math.round(v) : undefined;
        })(),
        purchasePrice,
        salePrice,
        purchasePriceHistory: Array.isArray(m.purchasePriceHistory ?? m.purchase_price_history)
          ? (m.purchasePriceHistory ?? m.purchase_price_history)
              .map((v) => Number(v))
              .filter((v) => Number.isFinite(v) && v >= 0)
          : [],
        salePriceHistory: Array.isArray(m.salePriceHistory ?? m.sale_price_history)
          ? (m.salePriceHistory ?? m.sale_price_history)
              .map((v) => Number(v))
              .filter((v) => Number.isFinite(v) && v >= 0)
          : [],
        saleUnit: baseUnit,
        expiryDate: (() => {
          const exp =
            m.expiryDate ??
            m.expiry_date ??
            m.expiresAt ??
            m.expires_at ??
            m.expirationDate ??
            m.expiration_date ??
            m.validUntil ??
            m.valid_until ??
            p?.expiryDate ??
            p?.expiry_date ??
            p?.expiresAt ??
            p?.expires_at;
          if (exp == null || String(exp).trim() === '') return null;
          return String(exp).trim().slice(0, 10);
        })(),
        imageUrl: resolveMediaUrl(
          m.imageUrl ?? m.image_url ?? m.image ?? m.photo ?? m.photo_url ?? p?.imageUrl ?? p?.image_url ?? p?.image ?? ''
        ),
        freePriceEnabled: Boolean(m.freePriceEnabled ?? m.free_price_enabled),
        /** Tahrirlash modali — openEdit(row.saleChoices) */
        saleChoices: saleOptions,
        /** FIFO: aktiv partiyalar (oldest first) */
        batches,
        fifoPurchasePrice: fifoPurchase ?? null,
        /** FIFO: eng qadimgi aktiv partiyaning sotish narxi */
        fifoSalePrice: (() => {
          const fp = item.fifo_sale_price ?? m.fifoSalePrice ?? item.fifo_sale_price;
          if (fp != null && Number.isFinite(Number(fp))) return Number(fp);
          if (fifoFromBatch?.salePrice > 0) return fifoFromBatch.salePrice;
          return null;
        })(),
        batchStockValueTotal: batchStockValueTotal ?? null,
      };
    });

    if (rows.length === 0 && (role === 'tenant_owner' || role === 'cashier')) {
      return getWarehouseOverviewFromTenants(params);
    }

    // Backend overview endi product_variants dan sale_options qaytaradi — har bir tovar uchun
    // alohida GET /products/:id chaqirish minglab so'rov va 30s+ kechikish keltirardi.
    return rows;
  } catch (err) {
    // tenant_owner va cashier uchun /api/warehouse/overview xato qaytarsa,
    // getWarehouseOverviewFromTenants fallback bilan urinamiz.
    if (role === 'tenant_owner' || role === 'cashier') {
      return getWarehouseOverviewFromTenants({ ...(params || {}), role });
    }
    throw err;
  }
}

/**
 * Backend tenants + tenant detail orqali ombor yozuvlarini yig'ish (overview bo'sh qaytsa).
 * Optimallashtirilgan: parallel so'rovlar (batch processing).
 */
export async function getWarehouseOverviewFromTenants(params = {}) {
  const role = String(params?.role ?? '').trim();
  if (role === 'tenant_owner' || role === 'cashier') {
    let tenantId =
      params?.tenantId ??
      params?.tenant_id ??
      null;
    if ((tenantId == null || String(tenantId).trim() === '') && endpoints.user) {
      try {
        const meRes = await api.get(endpoints.user);
        const meRaw = meRes?.data ?? meRes;
        tenantId = meRaw?.tenant_id ?? meRaw?.tenantId ?? null;
      } catch {
        tenantId = null;
      }
    }
    if (tenantId == null || String(tenantId).trim() === '') return [];
    const mapRow = (p, tenantMeta) => ({
      tenantId: String(tenantMeta.id ?? tenantId),
      tenantName: tenantMeta.companyName || '—',
      regionId: String(tenantMeta.regionId ?? ''),
      region: tenantMeta.region || '—',
      districtId: String(tenantMeta.districtId ?? ''),
      district: tenantMeta.districtName || '—',
      branchId: String(p.branchId ?? p.branch_id ?? ''),
      branchName: p.branchName ?? p.branch_name ?? null,
      productId: (() => {
        const raw = p.id ?? p.product_id ?? '';
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? String(Math.round(n)) : '';
      })(),
      productName: p.name ?? p.product_name ?? '—',
      barcode: (p.barcode != null && String(p.barcode).trim() !== '') ? String(p.barcode) : null,
      stock: Number(p.quantity_remaining ?? p.stock ?? p.quantity ?? p.qty ?? p.remaining ?? 0),
      soldCount: Number(p.quantity_sold ?? p.soldCount ?? p.sold_count ?? p.sold ?? 0),
      purchasePrice: Number(p.purchasePrice ?? p.purchase_price ?? p.cost_price ?? p.costPrice ?? 0),
      salePrice: Number(p.salePrice ?? p.sale_price ?? 0),
      saleUnit: normalizeSaleUnit(
        p.saleUnit ?? p.sale_unit ?? p.unit_type ?? p.unitType ?? p.measure_unit ?? p.measureUnit
      ),
      expiryDate: (() => {
        const exp =
          p.expiryDate ??
          p.expiry_date ??
          p.expiresAt ??
          p.expires_at ??
          p.expirationDate ??
          p.expiration_date ??
          p.validUntil ??
          p.valid_until;
        if (exp == null || String(exp).trim() === '') return null;
        return String(exp).trim().slice(0, 10);
      })(),
      imageUrl: resolveMediaUrl(p.image_url ?? p.imageUrl ?? p.image ?? p.photo ?? p.photo_url ?? ''),
      freePriceEnabled: Boolean(p.freePriceEnabled ?? p.free_price_enabled),
      saleChoices: pickFirstSaleOptions(p.sale_options, p.saleOptions, p.sale_choices, p.saleChoices, p.variants),
    });

    try {
      // tenant_owner/cashier uchun eng to'g'ri fallback: bevosita shu tenantning products endpointi.
      const productsRes = await api.get(endpoints.tenantProducts(tenantId));
      const productsRows = normalizeListResponse(productsRes);
      let detail = null;
      try {
        detail = await getTenantDetail(tenantId);
      } catch {
        detail = null;
      }
      const tenantMeta = {
        id: detail?.id ?? tenantId,
        companyName: detail?.companyName ?? '—',
        regionId: detail?.regionId ?? '',
        region: detail?.region ?? '—',
        districtId: detail?.districtId ?? '',
        districtName: detail?.districtName ?? '—',
      };
      if (productsRows.length > 0) {
        return productsRows.map((p) => mapRow(p, tenantMeta));
      }

      const detailProducts = Array.isArray(detail?.products) ? detail.products : [];
      if (detailProducts.length > 0) {
        return detailProducts.map((p) => mapRow(p, tenantMeta));
      }
      // So'nggi fallback: tenant_owner scope bilan products endpoint.
      const ownProductsRes = await api.get(endpoints.products);
      const ownProducts = normalizeListResponse(ownProductsRes);
      return ownProducts.map((p) => mapRow(p, tenantMeta));
    } catch {
      try {
        const ownProductsRes = await api.get(endpoints.products);
        const ownProducts = normalizeListResponse(ownProductsRes);
        const tenantMeta = { id: tenantId, companyName: '—', regionId: '', region: '—', districtId: '', districtName: '—' };
        return ownProducts.map((p) => mapRow(p, tenantMeta));
      } catch {
        return [];
      }
    }
  }

  const tenants = await getTenants(params || {});
  if (!Array.isArray(tenants) || tenants.length === 0) return [];
  
  const results = [];
  // Parallel so'rovlar - har bir tenant uchun parallel yuklash
  // Batch size: 5 ta bir vaqtda (server yukini kamaytirish)
  const BATCH_SIZE = 5;
  for (let i = 0; i < tenants.length; i += BATCH_SIZE) {
    const batch = tenants.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(async (t) => {
      try {
        const detail = await getTenantDetail(t.id);
        const prods = detail?.products ?? [];
        return prods.map((p) => ({
          tenantId: String(t.id),
          tenantName: t.companyName || '—',
          regionId: String(t.regionId ?? ''),
          region: t.region || '—',
          districtId: String(t.districtId ?? ''),
          district: t.districtName || '—',
          branchId: String(p.branchId ?? p.branch_id ?? ''),
          branchName: p.branchName ?? p.branch_name ?? null,
          productId: p.id ?? '',
          productName: p.name ?? p.product_name ?? '—',
          barcode: (p.barcode != null && String(p.barcode).trim() !== '') ? String(p.barcode) : null,
          stock: Number(p.quantity_remaining ?? p.stock ?? p.quantity ?? p.qty ?? p.remaining ?? 0),
          soldCount: Number(p.quantity_sold ?? p.soldCount ?? p.sold_count ?? p.sold ?? 0),
          purchasePrice: Number(p.purchasePrice ?? p.purchase_price ?? p.cost_price ?? p.costPrice ?? 0),
          salePrice: Number(p.salePrice ?? p.sale_price ?? 0),
          saleUnit: normalizeSaleUnit(
            p.saleUnit ?? p.sale_unit ?? p.unit_type ?? p.unitType ?? p.measure_unit ?? p.measureUnit
          ),
          expiryDate: (() => {
            const exp =
              p.expiryDate ??
              p.expiry_date ??
              p.expiresAt ??
              p.expires_at ??
              p.expirationDate ??
              p.expiration_date ??
              p.validUntil ??
              p.valid_until;
            if (exp == null || String(exp).trim() === '') return null;
            return String(exp).trim().slice(0, 10);
          })(),
          imageUrl: resolveMediaUrl(p.image_url ?? p.imageUrl ?? p.image ?? p.photo ?? p.photo_url ?? ''),
          freePriceEnabled: Boolean(p.freePriceEnabled ?? p.free_price_enabled),
          saleChoices: pickFirstSaleOptions(p.sale_options, p.saleOptions, p.sale_choices, p.saleChoices, p.variants),
        }));
      } catch {
        // Biror tenant_ownerda xato bo'lsa davom etamiz
        return [];
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.flat());
  }
  
  return results;
}

function normalizeListResponse(res) {
  const raw = res?.data ?? res;
  const list = raw?.data ?? raw?.items ?? (Array.isArray(raw) ? raw : []);
  return Array.isArray(list) ? list : [];
}

function normalizeItemResponse(res) {
  const raw = res?.data ?? res;
  return raw?.data ?? raw;
}

export async function createWarehouseStockIn(payload) {
  const res = await api.post(endpoints.warehouseStockIn, payload ?? {});
  return mapKeys(normalizeItemResponse(res));
}

export async function createWarehouseStockOut(payload) {
  const res = await api.post(endpoints.warehouseStockOut, payload ?? {});
  return mapKeys(normalizeItemResponse(res));
}

export async function createWarehouseTransfer(payload) {
  const res = await api.post(endpoints.warehouseTransfers, payload ?? {});
  return mapKeys(normalizeItemResponse(res));
}

export async function getWarehouseInventoryAudit(params = {}) {
  const res = await api.get(endpoints.warehouseInventoryAudit, { params: params || {} });
  return normalizeListResponse(res).map((x) => mapKeys(x));
}

export async function getWarehouseExpiry(params = {}) {
  const res = await api.get(endpoints.warehouseExpiry, { params: params || {} });
  return normalizeListResponse(res).map((x) => mapKeys(x));
}

export async function scanWarehouseBarcode(payload) {
  const res = await api.post(endpoints.warehouseScan, payload ?? {});
  return mapKeys(normalizeItemResponse(res));
}
